import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  delta?: number;
  icon?: LucideIcon;
  tone?: 'default' | 'success' | 'warning' | 'destructive';
  className?: string;
}

export function StatCard({ label, value, hint, delta, icon: Icon, tone = 'default', className }: StatCardProps) {
  const toneBorder =
    tone === 'success'
      ? 'border-[hsl(var(--success))]/40'
      : tone === 'warning'
        ? 'border-[hsl(var(--warning))]/40'
        : tone === 'destructive'
          ? 'border-destructive/40'
          : undefined;

  return (
    <Card className={cn('overflow-hidden', toneBorder, className)}>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>{label}</span>
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />}
        </div>
        <div className="font-display text-2xl font-extrabold">{value}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {typeof delta === 'number' && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium',
                delta >= 0 ? 'text-[hsl(var(--success))]' : 'text-destructive',
              )}
            >
              {delta >= 0 ? (
                <ArrowUpRight className="h-3 w-3" />
              ) : (
                <ArrowDownRight className="h-3 w-3" />
              )}
              {Math.abs(delta).toFixed(1)}%
            </span>
          )}
          {hint && <span>{hint}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
